import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import {
  setStoreCommissionOverride,
  setStorePrimeDeliveryCompany,
  setStoreType,
  getDeliveryCompanies,
} from "../../api/adminService";
import {
  formatDate,
  SubscriptionStatusLabels,
  SubscriptionStatusColors,
  StoreTypeLabels,
  StoreTypeColors,
} from "./constants";

export function getStoreColumns(handlers) {
  const { handleApprove, handleViewAnalytics } = handlers;
  return [
    {
      field: "storeName",
      headerName: "Store",
      flex: 1.3,
      minWidth: 220,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.4} sx={{ py: 1, alignItems: "center" }}>
          <Avatar
            src={params.row.logoUrl || undefined}
            variant="rounded"
            sx={{ width: 36, height: 36, bgcolor: "primary.main" }}
          >
            <StorefrontIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {params.row.city || "No city set"}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 150,
      renderCell: (params) => (
        <Chip
          size="small"
          color={StoreTypeColors[params.value] || "default"}
          variant={params.value === 1 ? "outlined" : "filled"}
          label={StoreTypeLabels[params.value] || "Retail"}
        />
      ),
    },
    { field: "ownerName", headerName: "Owner", flex: 1, minWidth: 160 },
    {
      field: "productCount",
      headerName: "Products",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "orderCount",
      headerName: "Orders",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "isApproved",
      headerName: "Approval",
      width: 130,
      renderCell: (params) =>
        params.value ? (
          <Chip size="small" color="success" variant="outlined" icon={<CheckCircleRoundedIcon />} label="Approved" />
        ) : (
          <Chip size="small" color="warning" variant="outlined" icon={<CancelRoundedIcon />} label="Pending" />
        ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip size="small" color="success" label="Active" />
        ) : (
          <Chip size="small" color="error" label="Inactive" />
        ),
    },
    {
      field: "isPrime",
      headerName: "Delivery",
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip size="small" color="secondary" icon={<BoltRoundedIcon />} label="Prime" />
        ) : (
          <Chip size="small" variant="outlined" label="Standard" />
        ),
    },
    {
      field: "primeDeliveryCompanyName",
      headerName: "Delivery Company",
      width: 170,
      valueGetter: (v) => v || "Not assigned",
    },
    {
      field: "subscriptionStartDate",
      headerName: "Subscription Start",
      width: 160,
      valueFormatter: (v) => formatDate(v),
    },
    {
      field: "subscriptionEndDate",
      headerName: "Subscription End",
      width: 160,
      valueFormatter: (v) => formatDate(v),
    },
    {
      field: "isComingSoon",
      headerName: "Visibility",
      width: 130,
      renderCell: (params) =>
        params.value ? (
          <Chip size="small" color="info" variant="outlined" label="Coming Soon" />
        ) : (
          <Chip size="small" color="success" variant="outlined" label="Ready" />
        ),
    },
    {
      field: "actions",
      headerName: "",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ width: "100%", alignItems: "center", justifyContent: "flex-end" }}
          onClick={(e) => e.stopPropagation()}
        >
          {!params.row.isApproved && (
            <Button size="small" variant="contained" onClick={() => handleApprove(params.row.storeId, true)}>
              Approve
            </Button>
          )}
          <Tooltip title="View analytics">
            <IconButton size="small" onClick={() => handleViewAnalytics(params.row.storeId)}>
              <InsightsRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <StoreRowMenu row={params.row} handlers={handlers} />
        </Stack>
      ),
    },
  ];
}

function StoreRowMenu({ row, handlers }) {
  const { handleApprove, handleToggleActive, handleToggleIsPrime, handleToggleComingSoon } = handlers;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  function close() {
    setAnchorEl(null);
  }

  function run(action) {
    close();
    action();
  }

  return (
    <>
      <Tooltip title="More actions">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={close} onClick={(e) => e.stopPropagation()}>
        {row.isApproved && (
          <MenuItem onClick={() => run(() => handleApprove(row.storeId, false))}>
            <ListItemIcon>
              <CancelRoundedIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Revoke approval</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => run(() => handleToggleActive(row.storeId, !row.isActive))}>
          <ListItemIcon>
            <PowerSettingsNewRoundedIcon fontSize="small" color={row.isActive ? "error" : "success"} />
          </ListItemIcon>
          <ListItemText>{row.isActive ? "Deactivate store" : "Activate store"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => run(() => handleToggleIsPrime(row.storeId, !row.isPrime))}>
          <ListItemIcon>
            <BoltRoundedIcon fontSize="small" color={row.isPrime ? "warning" : "secondary"} />
          </ListItemIcon>
          <ListItemText>{row.isPrime ? "Remove Prime delivery" : "Make Prime delivery"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => run(() => handleToggleComingSoon(row.storeId, !row.isComingSoon))}>
          <ListItemIcon>
            {row.isComingSoon ? (
              <VisibilityRoundedIcon fontSize="small" color="success" />
            ) : (
              <VisibilityOffRoundedIcon fontSize="small" color="info" />
            )}
          </ListItemIcon>
          <ListItemText>{row.isComingSoon ? "Mark ready" : "Mark coming soon"}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export function StoreDetailDrawer({ detail, onClose, handlers, onSaved }) {
  const { handleApprove, handleToggleActive, handleToggleIsPrime, handleToggleComingSoon, handleViewAnalytics } = handlers;

  return (
    <Drawer anchor="right" open={Boolean(detail)} onClose={onClose}>
      {detail && (
        <Box sx={{ width: 400, display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2.5,
              background: "linear-gradient(135deg, #0B2B26 0%, #113B36 100%)",
              color: "#fff",
              position: "relative",
            }}
          >
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: "absolute", top: 12, right: 12, color: "rgba(255,255,255,0.8)" }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Avatar
                src={detail.logoUrl || undefined}
                variant="rounded"
                sx={{ width: 56, height: 56, bgcolor: "rgba(255,255,255,0.15)" }}
              >
                <StorefrontIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" noWrap>
                  {detail.storeName}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", opacity: 0.85 }}>
                  <LocationOnRoundedIcon sx={{ fontSize: 15 }} />
                  <Typography variant="body2" noWrap>
                    {detail.city || "No city set"}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mt: 2 }}>
              {detail.isApproved ? (
                <Chip size="small" color="success" icon={<CheckCircleRoundedIcon />} label="Approved" />
              ) : (
                <Chip size="small" color="warning" icon={<CancelRoundedIcon />} label="Pending approval" />
              )}
              {detail.isActive ? (
                <Chip size="small" color="success" variant="outlined" label="Active" sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }} />
              ) : (
                <Chip size="small" color="error" label="Inactive" />
              )}
              <Chip
                size="small"
                color={StoreTypeColors[detail.type] || "default"}
                variant={detail.type === 1 ? "outlined" : "filled"}
                label={StoreTypeLabels[detail.type] || "Retail"}
                sx={detail.type === 1 ? { color: "#fff", borderColor: "rgba(255,255,255,0.4)" } : undefined}
              />
              {detail.isPrime && <Chip size="small" color="secondary" icon={<BoltRoundedIcon />} label="Prime" />}
              {detail.isComingSoon && (
                <Chip size="small" color="info" label="Coming Soon" />
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
              <MetricTile icon={Inventory2RoundedIcon} label="Products" value={detail.productCount} />
              <MetricTile icon={ReceiptLongRoundedIcon} label="Orders" value={detail.orderCount} />
              <MetricTile icon={StarRoundedIcon} label="Rating" value={detail.rating ?? "-"} />
            </Stack>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<InsightsRoundedIcon />}
              sx={{ mb: 3 }}
              onClick={() => handleViewAnalytics(detail.storeId)}
            >
              View Analytics
            </Button>

            <SectionLabel>Owner</SectionLabel>
            <Stack spacing={1.25} sx={{ mb: 2.5 }}>
              <DetailRow label="Name" value={detail.ownerName} />
              <DetailRow icon={EmailRoundedIcon} label="Email" value={detail.ownerEmail} />
              <DetailRow icon={PhoneRoundedIcon} label="Phone" value={detail.ownerPhone || "-"} />
            </Stack>

            <SectionLabel>Store details</SectionLabel>
            <Stack spacing={1.25} sx={{ mb: 2.5 }}>
              <DetailRow label="Joined" value={formatDate(detail.createdAt)} />
              <DetailRow
                label="Categories"
                value={detail.categoryNames.length ? detail.categoryNames.join(", ") : "-"}
              />
              {detail.primeDeliveryCompanyName && (
                <DetailRow label="Delivery Company" value={detail.primeDeliveryCompanyName} />
              )}
              {detail.subscriptionStatus && (
                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Subscription
                  </Typography>
                  <Chip
                    size="small"
                    label={SubscriptionStatusLabels[detail.subscriptionStatus] || detail.subscriptionStatus}
                    color={SubscriptionStatusColors[detail.subscriptionStatus] || "default"}
                  />
                </Stack>
              )}
              {detail.subscriptionStartDate && (
                <DetailRow label="Subscription Start" value={formatDate(detail.subscriptionStartDate)} />
              )}
              {detail.subscriptionEndDate && (
                <DetailRow label="Subscription End" value={formatDate(detail.subscriptionEndDate)} />
              )}
            </Stack>

            <Divider sx={{ mb: 2.5 }} />

            <SectionLabel>Store type</SectionLabel>
            <StoreTypeField storeId={detail.storeId} value={detail.type} onSaved={onSaved} />

            <Divider sx={{ my: 2.5 }} />

            <SectionLabel>Commission</SectionLabel>
            <CommissionOverrideField
              storeId={detail.storeId}
              value={detail.commissionOverridePercent}
              onSaved={onSaved}
            />

            {detail.isPrime && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <SectionLabel>Prime delivery partner</SectionLabel>
                <PrimeDeliveryCompanyField
                  storeId={detail.storeId}
                  value={detail.primeDeliveryCompanyUserId}
                  onSaved={onSaved}
                />
              </>
            )}
          </Box>

          <Box sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
              {!detail.isApproved && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CheckCircleRoundedIcon />}
                  onClick={() => handleApprove(detail.storeId, true)}
                >
                  Approve Store
                </Button>
              )}
              <Button
                variant="outlined"
                color={detail.isActive ? "error" : "success"}
                sx={{ flex: 1, minWidth: 140 }}
                onClick={() => handleToggleActive(detail.storeId, !detail.isActive)}
              >
                {detail.isActive ? "Deactivate" : "Activate"}
              </Button>
              <Button
                variant="outlined"
                color={detail.isPrime ? "warning" : "secondary"}
                sx={{ flex: 1, minWidth: 140 }}
                onClick={() => handleToggleIsPrime(detail.storeId, !detail.isPrime)}
              >
                {detail.isPrime ? "Remove Prime" : "Make Prime"}
              </Button>
              <Button
                variant="outlined"
                color={detail.isComingSoon ? "info" : "inherit"}
                sx={{ flex: 1, minWidth: 140 }}
                onClick={() => handleToggleComingSoon(detail.storeId, !detail.isComingSoon)}
              >
                {detail.isComingSoon ? "Mark Ready" : "Mark Coming Soon"}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

function MetricTile({ icon: Icon, label, value }) {
  return (
    <Box
      sx={{
        flex: 1,
        textAlign: "center",
        py: 1.25,
        borderRadius: 2,
        bgcolor: "background.default",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Icon sx={{ fontSize: 18, color: "text.secondary", mb: 0.25 }} />
      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1, display: "block" }}
    >
      {children}
    </Typography>
  );
}

function CommissionOverrideField({ storeId, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await setStoreCommissionOverride(storeId, input === "" ? null : Number(input));
      setEditing(false);
      onSaved();
    } catch {
      setError("Could not save the commission override.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {!editing ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.25,
            borderRadius: 2,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {value != null ? `${value}%` : "Using global rate"}
          </Typography>
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            size="small"
            type="number"
            placeholder="Global rate"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{ width: 120 }}
          />
          <Button size="small" variant="contained" disabled={saving} onClick={handleSave}>
            Save
          </Button>
          <Button size="small" disabled={saving} onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </Stack>
      )}
    </Box>
  );
}

function StoreTypeField({ storeId, value, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleChange(e) {
    const next = Number(e.target.value);
    setSaving(true);
    setError("");
    try {
      await setStoreType(storeId, next);
      onSaved();
    } catch {
      setError("Could not save the store type.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <FormControl size="small" fullWidth disabled={saving}>
        <InputLabel>Store type</InputLabel>
        <Select label="Store type" value={value ?? 1} onChange={handleChange}>
          {Object.entries(StoreTypeLabels).map(([type, label]) => (
            <MenuItem key={type} value={Number(type)}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

function PrimeDeliveryCompanyField({ storeId, value, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getDeliveryCompanies()
      .then(setCompanies)
      .catch(() => {});
  }, []);

  async function handleChange(e) {
    const next = e.target.value;
    setSaving(true);
    setError("");
    try {
      await setStorePrimeDeliveryCompany(storeId, next === "" ? null : Number(next));
      onSaved();
    } catch {
      setError("Could not save the assigned delivery company.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <FormControl size="small" fullWidth disabled={saving}>
        <InputLabel>Delivery company</InputLabel>
        <Select label="Delivery company" value={value ?? ""} onChange={handleChange}>
          <MenuItem value="">Not assigned</MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.userId} value={c.userId}>
              {c.companyName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        {Icon && <Icon sx={{ fontSize: 15, color: "text.secondary" }} />}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: "right", maxWidth: 220 }} noWrap>
        {value}
      </Typography>
    </Stack>
  );
}
